import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PersonAddAlt1 as PersonAddIcon,
} from '@mui/icons-material';
import {
  alpha,
  Card,
  CardContent,
  Chip,
  Container,
  Typography,
  Paper,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Stack,
  IconButton,
} from '@mui/material';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import * as api from '../../services/api';
import { PASSWORD_VALIDATION_HINT, validatePassword } from '../../utils/passwordValidation';
import { useI18n } from '../../i18n/useI18n';

const PAGE_SIZE = 10;

const createFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    bgcolor: 'common.white',
    '& fieldset': {
      borderColor: 'divider',
    },
    '&:hover fieldset': {
      borderColor: 'primary.main',
    },
    '&.Mui-focused fieldset': {
      borderWidth: 1.5,
    },
  },
};

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const { admin: a, pages: t } = useI18n();
  const [users, setUsers] = useState<api.AdminUserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0, blocked: 0, recent: 0 });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreRef = useRef(false);
  const fetchIdRef = useRef(0);

  const fetchUsers = useCallback(async (pageNumber: number, append: boolean) => {
    const fetchId = append ? fetchIdRef.current : ++fetchIdRef.current;

    if (append) {
      if (loadingMoreRef.current) return;
      loadingMoreRef.current = true;
      setLoadingMore(true);
    } else {
      loadingMoreRef.current = false;
      setLoadingMore(false);
      setLoading(true);
    }

    try {
      const response = await api.getAdminUsers(pageNumber, PAGE_SIZE, searchTerm);
      if (fetchId !== fetchIdRef.current) return;
      setUsers((prev) => (append ? [...prev, ...response.data] : response.data));
      setStats(response.stats);
      setHasMore(response.pagination.hasNextPage);
      setPage(pageNumber);
    } catch (error) {
      if (fetchId !== fetchIdRef.current) return;
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
        loadingMoreRef.current = false;
      }
    }
  }, [searchTerm]);

  // Live search: mỗi lần gõ chữ đều cập nhật kết quả (debounce ngắn)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, 200);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchUsers(1, false);
  }, [fetchUsers]);

  useEffect(() => {
    // Đừng trigger load-more khi đang search/reload trang 1
    if (loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMoreRef.current) {
          fetchUsers(page + 1, true);
        }
      },
      { rootMargin: '240px' }
    );

    const sentinel = sentinelRef.current;
    if (sentinel) observer.observe(sentinel);

    return () => observer.disconnect();
  }, [loading, hasMore, page, fetchUsers]);

  const isSearching = loading && !!searchTerm;
  const isInitialLoading = loading && users.length === 0 && !searchTerm;

  const handleToggleStatus = async (userId: string) => {
    const current = users.find((user) => user._id === userId);
    if (!current) return;

    try {
      await api.toggleUserStatus(userId);
      const nextStatus = current.status === 'ACTIVE' ? 'BLOCKED' : 'ACTIVE';
      setUsers((prev) =>
        prev.map((user) => (user._id === userId ? { ...user, status: nextStatus } : user))
      );
      setStats((prev) => ({
        ...prev,
        active: nextStatus === 'ACTIVE' ? prev.active + 1 : Math.max(0, prev.active - 1),
        blocked: nextStatus === 'BLOCKED' ? prev.blocked + 1 : Math.max(0, prev.blocked - 1),
      }));
      toast.success('User status updated successfully');
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleViewUser = (userId: string) => {
    navigate(`/users/${userId}`);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  const handleCreateDialogOpen = () => {
    setCreateDialogOpen(true);
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
    setCreateForm({ email: '', password: '', confirmPassword: '' });
    setShowPassword(false);
  };

  const handleCreateSubmit = async () => {
    if (!createForm.email || !createForm.password) {
      toast.error('Vui lòng nhập email và mật khẩu');
      return;
    }

    if (createForm.password !== createForm.confirmPassword) {
      toast.error('Mật khẩu xác nhận không khớp');
      return;
    }

    const passwordError = validatePassword(createForm.password);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }

    try {
      setCreating(true);
      await api.createUser({
        email: createForm.email.trim(),
        password: createForm.password,
      });
      toast.success('Tạo tài khoản thành công');
      handleCreateDialogClose();
      fetchUsers(1, false);
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.error || 'Không thể tạo tài khoản');
    } finally {
      setCreating(false);
    }
  };

  if (isInitialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 1.5, md: 3 },
        height: { xs: 'calc(100dvh - 56px)', sm: 'calc(100dvh - 64px)' },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
        '@keyframes umSoftIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* Sticky: hero + stats + search (mobile dính dưới navbar) */}
      <Box
        sx={{
          flexShrink: 0,
          mb: { xs: 1.5, md: 2 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: { xs: 1.25, md: 3 },
            p: { xs: 2, md: 3 },
            borderRadius: 4,
            color: 'common.white',
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 45%, ${theme.palette.secondary.main} 100%)`,
            boxShadow: (theme) => `0 20px 45px ${alpha(theme.palette.primary.main, 0.28)}`,
            animation: 'umSoftIn 0.5s ease both',
          }}
        >
          <Stack
            direction="row"
            spacing={1.25}
            justifyContent="space-between"
            alignItems="center"
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="overline"
                sx={{ letterSpacing: 1.2, opacity: 0.85, display: { xs: 'none', sm: 'block' } }}
              >
                Admin Dashboard
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: { xs: 0, md: 0.5 }, fontSize: { xs: '1.35rem', sm: '1.5rem', md: '2.125rem' }, lineHeight: 1.2 }}>
                {a.userManagement.title}
              </Typography>
              <Typography sx={{ maxWidth: 620, opacity: 0.9, display: { xs: 'none', sm: 'block' } }}>
                Theo dõi tài khoản khách hàng, trạng thái hoạt động và tạo nhanh người dùng mới
                trong một giao diện gọn gàng hơn.
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateDialogOpen}
              sx={{
                flexShrink: 0,
                minWidth: 0,
                bgcolor: 'common.white',
                color: 'primary.main',
                borderRadius: 999,
                px: { xs: 1.5, md: 2 },
                py: { xs: 0.75, md: 1 },
                fontWeight: 700,
                whiteSpace: 'nowrap',
                boxShadow: '0 10px 24px rgba(0,0,0,0.14)',
                '&:hover': {
                  bgcolor: 'grey.100',
                },
                '& .MuiButton-startIcon': { mr: 0.5 },
              }}
            >
              {a.userManagement.createAccount}
            </Button>
          </Stack>
        </Paper>

        <Box
          sx={{
            mb: { xs: 1.25, md: 3 },
            display: 'grid',
            gap: { xs: 1, sm: 2 },
            gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(3, 1fr)', md: 'repeat(3, 1fr)' },
          }}
        >
          {[
            {
              label: 'Tổng người dùng',
              shortLabel: 'Tổng',
              value: stats.total,
              tone: 'primary.main',
              bg: (theme: any) => alpha(theme.palette.primary.main, 0.08),
            },
            {
              label: 'Đang hoạt động',
              shortLabel: 'Hoạt động',
              value: stats.active,
              tone: 'success.main',
              bg: (theme: any) => alpha(theme.palette.success.main, 0.1),
            },
            {
              label: 'Đã chặn',
              shortLabel: 'Đã chặn',
              value: stats.blocked,
              tone: 'error.main',
              bg: (theme: any) => alpha(theme.palette.error.main, 0.1),
            },
          ].map((item, index) => (
            <Card
              key={item.label}
              elevation={0}
              sx={{
                borderRadius: { xs: 2, md: 3 },
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: item.bg,
                minWidth: 0,
                animation: 'umSoftIn 0.45s ease both',
                animationDelay: `${0.06 + index * 0.05}s`,
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 1, sm: 2.25 },
                  '&:last-child': { pb: { xs: 1, sm: 2.25 } },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: { xs: 0.25, sm: 1 },
                    fontSize: { xs: '0.65rem', sm: '0.875rem' },
                    lineHeight: 1.25,
                    whiteSpace: { xs: 'nowrap', sm: 'normal' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    {item.shortLabel}
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {item.label}
                  </Box>
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: item.tone,
                    fontSize: { xs: '1.15rem', sm: '2.125rem' },
                    lineHeight: 1.2,
                  }}
                >
                  {item.value}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Ô tìm kiếm đẹp + nút hủy lọc */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.25, md: 1.5 },
            borderRadius: 4,
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
            background: (theme) =>
              `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.common.white, 0.95)} 100%)`,
            boxShadow: (theme) => `0 10px 28px ${alpha(theme.palette.primary.main, 0.12)}`,
            animation: 'umSoftIn 0.45s ease both',
            animationDelay: '0.24s',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Tìm theo tên hoặc email..."
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 999,
                  bgcolor: 'common.white',
                  pr: 0.5,
                  boxShadow: (theme) => `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.08)}`,
                  '& fieldset': { borderColor: 'transparent' },
                  '&:hover fieldset': { borderColor: (theme) => alpha(theme.palette.primary.main, 0.35) },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1.5 },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {(isSearching || (loading && searchInput.trim() !== searchTerm)) ? (
                      <CircularProgress size={16} sx={{ mr: 0.75 }} />
                    ) : searchInput ? (
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        aria-label="Hủy lọc"
                        sx={{
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                          '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.18) },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    ) : null}
                  </InputAdornment>
                ),
              }}
            />
            {!!searchInput && (
              <Button
                onClick={handleClearSearch}
                variant="outlined"
                size="small"
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  flexShrink: 0,
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 1.75,
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.35),
                  color: 'primary.main',
                }}
              >
                Hủy lọc
              </Button>
            )}
          </Stack>
        </Paper>
      </Box>

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          animation: 'umSoftIn 0.5s ease both',
          animationDelay: '0.3s',
        }}
      >
        <Box
          sx={{
            px: 2.25,
            py: 1.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', md: '1.25rem' } }}>
            Danh sách người dùng
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {/* Mobile: card list — hiện đủ thông tin không bị cắt cột */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1.5 }}>
          <Stack spacing={1.25}>
            {users.map((user, index) => (
              <Box
                key={user._id}
                onClick={() => handleViewUser(user._id)}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  cursor: 'pointer',
                  animation: 'umSoftIn 0.4s ease both',
                  animationDelay: `${0.34 + Math.min(index, 12) * 0.04}s`,
                  '&:active': {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                  <Avatar
                    src={user.avatarUrl || undefined}
                    alt={user.username}
                    sx={{ width: 44, height: 44, bgcolor: 'primary.main', fontSize: '1.05rem', flexShrink: 0 }}
                  >
                    {!user.avatarUrl ? user.username?.charAt(0)?.toUpperCase() : null}
                  </Avatar>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {user.username}
                      </Typography>
                      <Chip
                        label={user.role}
                        size="small"
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          fontSize: '0.68rem',
                          borderRadius: 1.5,
                          bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
                          color: 'info.dark',
                          flexShrink: 0,
                        }}
                      />
                    </Stack>

                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        mt: 0.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.8rem',
                      }}
                    >
                      {user.email}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mt: 1.25 }}
                    >
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {format(new Date(user.createdAt), 'dd/MM/yyyy HH:mm')}
                      </Typography>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleStatus(user._id);
                        }}
                        size="small"
                        variant="contained"
                        title={user.status === 'ACTIVE' ? a.userManagement.blockUser : a.userManagement.activateUser}
                        sx={{
                          minWidth: 78,
                          borderRadius: '999px',
                          textTransform: 'uppercase',
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          py: 0.35,
                          px: 1.25,
                          color: 'common.white',
                          bgcolor: user.status === 'ACTIVE' ? 'success.main' : 'error.main',
                          '&:hover': {
                            bgcolor: user.status === 'ACTIVE' ? 'success.dark' : 'error.dark',
                          },
                        }}
                      >
                        {user.status}
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            ))}

            {users.length === 0 && !loading && (
              <Box sx={{ py: 5, px: 2, textAlign: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {searchTerm ? `Không tìm thấy kết quả cho "${searchTerm}"` : 'Không tìm thấy người dùng phù hợp'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {searchTerm
                    ? 'Thử gõ ít chữ hơn, ví dụ nhập "lao" để tìm Haidilao.'
                    : 'Thử đổi từ khoá tìm kiếm hoặc tạo tài khoản mới.'}
                </Typography>
              </Box>
            )}

            {loading && users.length === 0 && !!searchTerm && (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} />
              </Box>
            )}
          </Stack>
        </Box>

        {/* Desktop: table đầy đủ cột */}
        <TableContainer component={Box} sx={{ display: { xs: 'none', md: 'block' }, overflow: 'visible' }}>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: 'grey.50',
                '& .MuiTableCell-head': {
                  fontWeight: 700,
                  color: 'text.primary',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
              }}
            >
              <TableCell sx={{ py: 1.75 }}>{a.userManagement.colUsername}</TableCell>
              <TableCell sx={{ py: 1.75 }}>{a.userManagement.colEmail}</TableCell>
              <TableCell sx={{ py: 1.75 }}>{a.userManagement.colRole}</TableCell>
              <TableCell sx={{ py: 1.75 }}>{a.userManagement.colCreatedAt}</TableCell>
              <TableCell sx={{ py: 1.75 }} align="center">{a.userManagement.colStatus}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user, index) => (
              <TableRow
                key={user._id}
                onClick={() => handleViewUser(user._id)}
                sx={{
                  cursor: 'pointer',
                  animation: 'umSoftIn 0.4s ease both',
                  animationDelay: `${0.34 + Math.min(index, 12) * 0.04}s`,
                  '& .MuiTableCell-body': {
                    py: 2.25,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  },
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.035),
                  },
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                      src={user.avatarUrl || undefined}
                      alt={user.username}
                      sx={{ width: 38, height: 38, bgcolor: 'primary.main', fontSize: '1rem' }}
                    >
                      {!user.avatarUrl ? user.username?.charAt(0)?.toUpperCase() : null}
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>
                      {user.username}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={user.role}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      borderRadius: 2,
                      bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
                      color: 'info.dark',
                    }}
                  />
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{format(new Date(user.createdAt), 'dd/MM/yyyy HH:mm')}</TableCell>
                <TableCell align="center">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleStatus(user._id);
                    }}
                    size="small"
                    variant="contained"
                    title={user.status === 'ACTIVE' ? a.userManagement.blockUser : a.userManagement.activateUser}
                    sx={{
                      ml: 0.5,
                      minWidth: 90,
                      borderRadius: '999px',
                      textTransform: 'uppercase',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      py: 0.45,
                      px: 1.5,
                      color: 'common.white',
                      justifyContent: 'center',
                      bgcolor: user.status === 'ACTIVE' ? 'success.main' : 'error.main',
                      '&:hover': {
                        bgcolor: user.status === 'ACTIVE' ? 'success.dark' : 'error.dark',
                      },
                    }}
                  >
                    {user.status}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {searchTerm ? `Không tìm thấy kết quả cho "${searchTerm}"` : 'Không tìm thấy người dùng phù hợp'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {searchTerm
                      ? 'Thử gõ ít chữ hơn, ví dụ nhập "lao" để tìm Haidilao.'
                      : 'Thử đổi từ khoá tìm kiếm hoặc tạo tài khoản mới.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {loading && users.length === 0 && !!searchTerm && (
              <TableRow>
                <TableCell colSpan={5} sx={{ py: 4, textAlign: 'center', borderBottom: 'none' }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </TableContainer>

        {(hasMore || loadingMore) && users.length > 0 && (
          <Box ref={sentinelRef} sx={{ py: 1.5, px: 2 }}>
            {loadingMore ? (
              <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                <CircularProgress size={22} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Đang tải thêm...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ height: 1 }} />
            )}
          </Box>
        )}
        </Box>
      </Paper>

      <Dialog
        open={createDialogOpen}
        onClose={creating ? undefined : handleCreateDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: (theme) => `0 24px 64px ${alpha(theme.palette.primary.main, 0.28)}`,
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 2.25,
            color: 'common.white',
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.secondary.main} 100%)`,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha('#fff', 0.18),
                }}
              >
                <PersonAddIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  {a.userManagement.dialogTitle}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.88 }}>
                  Tạo tài khoản khách hàng mới
                </Typography>
              </Box>
            </Stack>
            <IconButton
              onClick={handleCreateDialogClose}
              disabled={creating}
              sx={{ color: 'common.white', bgcolor: alpha('#fff', 0.12), '&:hover': { bgcolor: alpha('#fff', 0.22) } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pt: 2.5, pb: 1 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label={a.userManagement.email}
              type="email"
              autoComplete="email"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              required
              sx={createFieldSx}
            />
            <TextField
              fullWidth
              label={a.userManagement.password}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              required
              sx={createFieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                    >
                      {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2.5,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.45, display: 'block' }}>
                {PASSWORD_VALIDATION_HINT}
              </Typography>
            </Box>
            <TextField
              fullWidth
              label={a.userManagement.confirmPassword}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={createForm.confirmPassword}
              onChange={(e) => setCreateForm({ ...createForm, confirmPassword: e.target.value })}
              required
              error={!!createForm.confirmPassword && createForm.confirmPassword !== createForm.password}
              helperText={
                createForm.confirmPassword && createForm.confirmPassword !== createForm.password
                  ? 'Mật khẩu xác nhận chưa khớp'
                  : ' '
              }
              sx={createFieldSx}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, pb: 2.5, pt: 0.5, gap: 1 }}>
          <Button
            onClick={handleCreateDialogClose}
            disabled={creating}
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 700,
              px: 2.25,
              color: 'text.secondary',
            }}
          >
            {t.common.cancel}
          </Button>
          <Button
            onClick={handleCreateSubmit}
            variant="contained"
            disabled={creating}
            startIcon={creating ? undefined : <AddIcon />}
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 800,
              px: 2.75,
              py: 1,
              boxShadow: (theme) => `0 10px 22px ${alpha(theme.palette.primary.main, 0.35)}`,
            }}
          >
            {creating ? <CircularProgress size={20} color="inherit" /> : a.userManagement.submitCreate}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserManagement;
